<script setup lang="ts">
/**
 * App Sidebar Component
 *
 * Main navigation sidebar
 */
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue';
import { X } from 'lucide-vue-next';
import { useUIStore, useAuthStore, useSettingsStore, useEntitlementsStore } from '@/stores';
import { Icon, HelpDeskDialog, useHelpDesk } from '@tms-phoenix/ui-vue';
import { useHelpDeskSubmit } from '@/shared/composables/useHelpDeskSubmit';
import { useTenantSupportEmail } from '@/shared/composables/useTenantSupportEmail';
import { firstIncompleteStep } from '@/modules/settings/wizardSteps';
import NeedHelpDialog from '@/modules/auth/components/NeedHelpDialog.vue';
import { useTranslation } from 'i18next-vue';

const { t } = useTranslation();
const route = useRoute();
const router = useRouter();
const uiStore = useUIStore();
const authStore = useAuthStore();
const settingsStore = useSettingsStore();
const entitlementsStore = useEntitlementsStore();

const submitTicket = useHelpDeskSubmit();
const helpDesk = useHelpDesk(submitTicket);
const { supportEmail, supportContactName, supportFacilityName, supportContactPhone, ensureSupportEmail } = useTenantSupportEmail();

const hospitalContact = computed(() => {
  const facilityName = supportFacilityName.value || null;
  const contactPerson = supportContactName.value || null;
  const contactPhone = supportContactPhone.value || null;
  const contactEmail = supportEmail.value || null;
  if (!facilityName && !contactPerson && !contactPhone && !contactEmail) return null;
  return { facilityName, contactPerson, contactPhone, contactEmail };
});

/** Roles that are allowed to submit a help-desk ticket. */
const TICKET_ROLES = ['TECHNICAL_ADMIN', 'CLINICAL_ADMIN'];

/** Controls the support-info dialog for non-ticket roles. */
const supportInfoVisible = ref(false);

const canSubmitTicket = computed(() =>
  TICKET_ROLES.some((role) => authStore.hasRole(role)),
);

async function openHelpDesk() {
  uiStore.closeSidebar();
  await ensureSupportEmail();
  if (canSubmitTicket.value) {
    helpDesk.open(authStore.userName ?? '', authStore.userEmail ?? '', route.fullPath);
  } else {
    supportInfoVisible.value = true;
  }
}

// Mothers is not part of the P1 package — hide it regardless of entitlement flags.
const isPlanP1 = computed(() => entitlementsStore.planFlag === 'P1');
// Inventory and Recall Management are not part of the P1 or P2 packages.
const isPlanP1OrP2 = computed(() => entitlementsStore.planFlag === 'P1' || entitlementsStore.planFlag === 'P2');

const navigation = computed(() => [
  { name: t('nav.dashboard'), href: '/', icon: 'Dashboard', show: true },
  { name: t('nav.patients'), href: '/patients', icon: 'Patients', show: entitlementsStore.hasEntitlement('PATIENT_MANAGEMENT') },
  { name: t('nav.mothers'), href: '/mothers', icon: 'Mothers', show: entitlementsStore.hasEntitlement('MOTHER_MANAGEMENT') && !isPlanP1.value },
  { name: t('nav.donors'), href: '/donors', icon: 'Donors', show: entitlementsStore.hasEntitlement('DONOR_MANAGEMENT') },
  { name: t('nav.inventory'), href: '/customer-inventory', icon: 'Prepare', iconVariant: 'stroke' as const, show: entitlementsStore.hasEntitlement('INVENTORY') && !isPlanP1OrP2.value },
  // Move Inventory is part of every package and outside RBAC/entitlements.
  // Hide only when the user has no effective role beyond Auditor/Default.
  { name: t('nav.moveInventory', 'Move Inventory'), href: '/move-inventory', icon: 'MoveInventory', iconVariant: 'stroke' as const, show: !authStore.hasOnlyRoles(['AUDITOR', 'DEFAULT']) },
  { name: t('nav.milk'), href: '/milk', icon: 'MilkInventory', show: entitlementsStore.hasEntitlement('MILK_MANAGEMENT') },
  /* ── Feed Processing ── */
  { name: t('nav.feedProcessing'), href: '/feed-processing', icon: 'AvailableDeposits', show: entitlementsStore.hasAnyEntitlement(['feed.feed_prep_full', 'feed.feed_prep_donor_only', 'feed.feed_prep_product', 'feed.feed_prep_donor_mother']) },
  /* ── Feed Administration ── */
  { name: t('feedAdmin.pageTitle'), href: '/feed-administration', icon: 'FeedAdministration', show: entitlementsStore.hasEntitlement('feed.feed_administer') || entitlementsStore.hasEntitlement('feed.feed_prep_full') },
  { name: t('recall.pageTitle'), href: '/recall', icon: 'RecallManagement', show: entitlementsStore.hasAllEntitlements(['INVENTORY', 'RECALL']) && !isPlanP1OrP2.value },
  { name: t('nav.reports'), href: '/reports', icon: 'Reports', show: entitlementsStore.hasEntitlement('REPORTS') },
  { name: t('users.title'), href: '/users', icon: 'Users', show: entitlementsStore.hasEntitlement('USER_MANAGEMENT') },
  { name: t('nav.auditLogs', 'Audit Logs'), href: '/audit-logs', icon: 'CreateOrder', show: entitlementsStore.hasEntitlement('AUDIT_LOG') },
  { name: t('nav.disposeArea'), href: '/dispose-area', icon: 'TrashBin', show: entitlementsStore.hasAnyEntitlement(['INVENTORY', 'MILK_MANAGEMENT', 'feed.feed_prep_full', 'feed.feed_prep_donor_only', 'feed.feed_prep_product', 'feed.feed_prep_donor_mother']) },
  // Interoperability is future scope — hidden regardless of entitlement until the feature ships.
  { name: t('nav.interoperability', 'Interoperability'), href: '/interoperability/user-management', icon: 'LinkChain', iconVariant: 'stroke', show: false },
].filter((item) => item.show));

// Settings is rendered as a toggleable panel button at the bottom of the nav — see template

function closeSidebar() {
  uiStore.closeSidebar();
}

function isActive(href: string) {
  if (href === '/') {
    return route.path === '/';
  }
  // Interoperability's sub-pages (User Management / Customization / Transaction
  // Monitoring) are navigated via in-page tabs, not separate sidebar items,
  // so this single entry stays active for any /interoperability/* route.
  if (href.startsWith('/interoperability')) {
    return route.path.startsWith('/interoperability');
  }
  return route.path.startsWith(href);
}

const isSettingsRoute = computed(() => route.path.startsWith('/app-settings'));

async function handleSettingsClick() {
  uiStore.closeSidebar();
  try {
    const onboarded = await settingsStore.checkSettingsOnboarding();
    // Same "resume" computation the checklist's contextual button uses, so
    // clicking this icon behaves identically to "Resume where I left off."
    await router.push(onboarded ? '/app-settings/dashboard' : firstIncompleteStep(settingsStore.completedSteps).path);
  } catch {
    // API failure — treat as onboarded so the settings area behaves normally
    settingsStore.settingsOnboarded = true;
    await router.push('/app-settings/dashboard');
  }
}
</script>

<template>
  <div>
    <!-- Mobile sidebar -->
    <TransitionRoot
      as="template"
      :show="uiStore.sidebarOpen"
    >
      <Dialog
        as="div"
        class="relative z-50 lg:hidden"
        @close="closeSidebar"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button
                    type="button"
                    class="-m-2.5 p-2.5"
                    data-testid="mobile-close-button"
                    @click="closeSidebar"
                  >
                    <span class="sr-only">{{ t('nav.closeSidebar') }}</span>
                    <X
                      class="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </TransitionChild>

              <!-- Mobile sidebar content -->
              <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
                <div class="flex h-16 shrink-0 items-center">
                  <h1 class="text-xl font-bold text-gray-900">
                    {{ t('nav.appTitle') }}
                  </h1>
                </div>
                <nav class="flex flex-1 flex-col relative">
                  <ul
                    role="list"
                    class="flex flex-1 flex-col gap-y-7"
                  >
                    <li>
                      <ul
                        role="list"
                        class="-mx-2 space-y-1"
                      >
                        <li
                          v-for="item in navigation"
                          :key="item.href"
                        >
                          <RouterLink
                            :to="item.href"
                            :class="[
                              isActive(item.href)
                                ? 'bg-profile-50 text-black font-semibold'
                                : 'text-gray-600 font-normal hover:bg-gray-50 hover:text-primary-600',
                              'group flex gap-x-3 rounded-md p-2 text-sm leading-6',
                            ]"
                            @click="closeSidebar"
                          >
                            <!-- <component
                              :is="item.icon"
                              class="h-6 w-6 shrink-0"
                              aria-hidden="true"
                            /> -->
                            <Icon
                              :name="item.icon"
                              size-class="h-6 w-6 shrink-0"
                              color="text-secondary"
                              :variant="item.iconVariant ?? 'fill'"
                            />
                            {{ item.name }}
                          </RouterLink>
                        </li>
                      </ul>
                    </li>

                    <!-- Bottom Navigation -->
                    <li class="mt-auto">
                      <ul
                        role="list"
                        class="-mx-2 space-y-1"
                      >
                        <!-- Help & Support — opens HelpDeskDialog -->
                        <li v-entitlement="'HELPDESK'">
                          <button
                            type="button"
                            class="w-full text-gray-600 font-normal hover:bg-gray-50 hover:text-primary-600 group flex gap-x-3 rounded-md p-2 text-sm leading-6 text-left cursor-pointer"
                            @click="openHelpDesk"
                          >
                            <Icon
                              name="HelpSupport"
                              size-class="h-6 w-6 shrink-0"
                              color="text-gray-600"
                            />
                            {{ t('nav.helpSupport') }}
                          </button>
                        </li>
                        <!-- Settings link -->
                        <li v-entitlement="'SETTINGS'">
                          <button
                            type="button"
                            :class="[
                              isSettingsRoute
                                ? 'bg-profile-50 text-black font-semibold'
                                : 'text-gray-600 font-normal hover:bg-gray-50 hover:text-primary-600',
                              'w-full text-left group flex gap-x-3 rounded-md p-2 text-sm leading-6 cursor-pointer',
                            ]"
                            @click="handleSettingsClick"
                          >
                            <Icon
                              name="Settings"
                              size-class="h-6 w-6 shrink-0"
                              color="text-secondary"
                            />
                            {{ t('nav.settings') }}
                          </button>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Desktop sidebar -->
    <div
      :class="[
        'hidden lg:fixed lg:top-16 lg:z-40 lg:flex lg:flex-col lg:mt-4 transition-all duration-300'
      ]"
      :style="{
        height: 'calc(100vh - 4rem - 1rem - 50px)',
        width: uiStore.sidebarCollapsed ? '60px' : '14rem'
      }"
    >
      <!-- :style="{ height: 'calc(100vh - 4rem - 1rem - 80px)' }" -->
      <div
        class="custom-sidebar flex grow flex-col gap-y-5 border-r border-gray-200 bg-white pb-4 pt-6"
        :class="uiStore.sidebarCollapsed ? 'px-3' : 'px-6'"
      >
        <nav class="flex flex-1 flex-col relative">
          <ul
            role="list"
            class="flex flex-1 flex-col gap-y-7"
          >
            <li>
              <ul
                role="list"
                :class="uiStore.sidebarCollapsed ? 'space-y-1' : '-mx-2 space-y-1'"
              >
                <li
                  v-for="item in navigation"
                  :key="item.href"
                >
                  <RouterLink
                    :to="item.href"
                    :class="[
                      isActive(item.href)
                        ? 'bg-profile-50 text-black font-semibold'
                        : 'text-gray-600 font-normal hover:bg-gray-50 hover:text-primary-600',
                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6',
                      uiStore.sidebarCollapsed ? 'justify-center' : ''
                    ]"
                    :title="uiStore.sidebarCollapsed ? item.name : ''"
                  >
                    <!-- <component
                      :is="item.icon"
                      class="h-6 w-6 shrink-0"
                      aria-hidden="true"
                    /> -->
                    <Icon
                      :name="item.icon"
                      size-class="h-6 w-6 shrink-0"
                      color="text-secondary"
                      :variant="item.iconVariant ?? 'fill'"
                    />
                    <span v-if="!uiStore.sidebarCollapsed">{{ item.name }}</span>
                  </RouterLink>
                </li>
              </ul>
            </li>

            <!-- Bottom Navigation -->
            <li class="mt-auto">
              <ul
                role="list"
                :class="uiStore.sidebarCollapsed ? 'space-y-1' : '-mx-2 space-y-1'"
              >
                <!-- Help & Support — opens HelpDeskDialog -->
                <li v-entitlement="'HELPDESK'">
                  <button
                    type="button"
                    :class="[
                      'w-full text-left text-gray-600 font-normal hover:bg-gray-50 hover:text-primary-600 group flex gap-x-3 rounded-md p-2 text-sm leading-6 cursor-pointer',
                      uiStore.sidebarCollapsed ? 'justify-center' : ''
                    ]"
                    :title="uiStore.sidebarCollapsed ? t('nav.helpSupport') : ''"
                    @click="openHelpDesk"
                  >
                    <Icon
                      name="HelpSupport"
                      size-class="h-6 w-6 shrink-0"
                      color="text-gray-600"
                    />
                    <span v-if="!uiStore.sidebarCollapsed">{{ t('nav.helpSupport') }}</span>
                  </button>
                </li>
                <!-- Settings link -->
                <li v-entitlement="'SETTINGS'">
                  <button
                    type="button"
                    :class="[
                      isSettingsRoute
                        ? 'bg-profile-50 text-black font-semibold'
                        : 'text-gray-600 font-normal hover:bg-gray-50 hover:text-primary-600',
                      'w-full text-left group flex gap-x-3 rounded-md p-2 text-sm leading-6 cursor-pointer',
                      uiStore.sidebarCollapsed ? 'justify-center' : '',
                    ]"
                    :title="uiStore.sidebarCollapsed ? t('nav.settings') : ''"
                    @click="handleSettingsClick"
                  >
                    <Icon
                      name="Settings"
                      size-class="h-6 w-6 shrink-0"
                      color="text-secondary"
                    />
                    <span v-if="!uiStore.sidebarCollapsed">{{ t('nav.settings') }}</span>
                  </button>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>

  <!-- Help Desk Dialog — only for TECHNICAL_ADMIN / CLINICAL_ADMIN -->
  <HelpDeskDialog
    v-model:visible="helpDesk.visible.value"
    :is-logged-in="helpDesk.isLoggedIn.value"
    :user-name="helpDesk.userName.value"
    :user-email="helpDesk.userEmail.value"
    :current-path="helpDesk.currentPath.value"
    :loading="helpDesk.loading.value"
    :auto-close-on-submit="false"
    :show-success-toast="false"
    :hospital-contact="hospitalContact"
    @submit="helpDesk.handleSubmit"
  />

  <!-- Support Info Dialog — for all other roles -->
  <NeedHelpDialog
    v-model:visible="supportInfoVisible"
    :facility-name="supportFacilityName"
    :contact-person="supportContactName"
    :contact-phone="supportContactPhone"
    :contact-email="supportEmail"
  />
</template>

<style scoped>
.custom-sidebar {
  border-radius: 0 8px 8px 0;
  background: white;
  box-shadow: 4px 0 4px 2px rgba(173, 143, 191, 0.10);
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  will-change: width;
}

/* Smoother scrollbar */
.custom-sidebar::-webkit-scrollbar {
  width: 6px;
}

.custom-sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-sidebar::-webkit-scrollbar-thumb {
  background: var(--color-secondary-200);
  border-radius: 3px;
}

.custom-sidebar::-webkit-scrollbar-thumb:hover {
  background: var(--color-secondary-300);
}
</style>
